import {
  ArrowRight,
  ArrowUpRight,
  Award,
  Banknote,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarDays,
  ChartNoAxesCombined,
  Check,
  ChevronDown,
  CircleDollarSign,
  ClipboardCheck,
  Clock3,
  Download,
  Facebook,
  FileCheck2,
  FileSearch,
  FileText,
  Globe2,
  Handshake,
  Instagram,
  Landmark,
  Layers3,
  Linkedin,
  LockKeyhole,
  Mail,
  MapPin,
  MessageCircle,
  Newspaper,
  Phone,
  Play,
  Plus,
  Route,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  Youtube,
  type LucideIcon,
} from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Dialog } from "./ui/overlays";
import styles from "./icon-picker.module.css";

type IconOption = {
  value: string;
  label: string;
  group: string;
  icon: LucideIcon;
};

const iconOptions: IconOption[] = [
  { value: "arrow-right", label: "Sağ ok", group: "Aksiyon", icon: ArrowRight },
  { value: "arrow-up-right", label: "Dış bağlantı", group: "Aksiyon", icon: ArrowUpRight },
  { value: "check", label: "Onay", group: "Aksiyon", icon: Check },
  { value: "plus", label: "Ekle", group: "Aksiyon", icon: Plus },
  { value: "play", label: "Oynat", group: "Aksiyon", icon: Play },
  { value: "send-03", label: "Gönder", group: "Aksiyon", icon: Send },
  { value: "download", label: "İndir", group: "Aksiyon", icon: Download },
  { value: "bank", label: "Banka", group: "Finans", icon: Landmark },
  { value: "banknote", label: "Banknot", group: "Finans", icon: Banknote },
  { value: "coins", label: "Finansman", group: "Finans", icon: CircleDollarSign },
  { value: "chart", label: "Finansal grafik", group: "Finans", icon: ChartNoAxesCombined },
  { value: "trend-up-01", label: "Yükselen trend", group: "Finans", icon: TrendingUp },
  { value: "target", label: "Hedef", group: "Finans", icon: Target },
  { value: "briefcase", label: "İş çantası", group: "Kurumsal", icon: BriefcaseBusiness },
  { value: "building", label: "Kuruluş", group: "Kurumsal", icon: Building2 },
  { value: "users", label: "Ekip", group: "Kurumsal", icon: Users },
  { value: "handshake", label: "İş birliği", group: "Kurumsal", icon: Handshake },
  { value: "award-01", label: "Başarı", group: "Kurumsal", icon: Award },
  { value: "book-open", label: "Kitap", group: "İçerik", icon: BookOpen },
  { value: "file", label: "Belge", group: "İçerik", icon: FileText },
  { value: "file-check-02", label: "Onaylı belge", group: "İçerik", icon: FileCheck2 },
  { value: "file-search", label: "Belge inceleme", group: "İçerik", icon: FileSearch },
  { value: "clipboard-check", label: "Kontrol listesi", group: "İçerik", icon: ClipboardCheck },
  { value: "newspaper", label: "Haber", group: "İçerik", icon: Newspaper },
  { value: "mail", label: "E-posta", group: "İletişim", icon: Mail },
  { value: "phone", label: "Telefon", group: "İletişim", icon: Phone },
  { value: "message", label: "Mesaj", group: "İletişim", icon: MessageCircle },
  { value: "globe", label: "Küresel ağ", group: "Konum", icon: Globe2 },
  { value: "map-pin", label: "Konum", group: "Konum", icon: MapPin },
  { value: "route", label: "Rota", group: "Konum", icon: Route },
  { value: "shield-check", label: "Güven", group: "Güvenlik", icon: ShieldCheck },
  { value: "lock-01", label: "Kilit", group: "Güvenlik", icon: LockKeyhole },
  { value: "calendar", label: "Takvim", group: "Zaman", icon: CalendarDays },
  { value: "calendar-check", label: "Planlanan tarih", group: "Zaman", icon: CalendarCheck },
  { value: "clock", label: "Saat", group: "Zaman", icon: Clock3 },
  { value: "layers", label: "Katmanlar", group: "Diğer", icon: Layers3 },
  { value: "sparkle", label: "Öne çıkan", group: "Diğer", icon: Sparkles },
  { value: "search", label: "Arama", group: "Diğer", icon: Search },
  { value: "linkedin", label: "LinkedIn", group: "Sosyal", icon: Linkedin },
  { value: "instagram", label: "Instagram", group: "Sosyal", icon: Instagram },
  { value: "facebook", label: "Facebook", group: "Sosyal", icon: Facebook },
  { value: "youtube", label: "YouTube", group: "Sosyal", icon: Youtube },
];

export function IconPicker({
  value,
  onChange,
  id,
  ariaLabel = "İkon seçin",
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchId = useId();
  const selected = iconOptions.find((option) => option.value === value);
  const SelectedIcon = selected?.icon ?? Sparkles;
  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase("tr-TR");
    if (!term) return iconOptions;
    return iconOptions.filter((option) =>
      `${option.label} ${option.group} ${option.value}`
        .toLocaleLowerCase("tr-TR")
        .includes(term),
    );
  }, [query]);

  function select(next: string) {
    onChange(next);
    setOpen(false);
    setQuery("");
  }

  return (
    <>
      <button id={id} type="button" className={styles.trigger} aria-label={ariaLabel} onClick={() => setOpen(true)}>
        <span className={styles.selectedIcon}><SelectedIcon size={18} aria-hidden="true" /></span>
        <span className={styles.selectedCopy}>
          <strong>{selected?.label ?? (value ? "Mevcut ikon" : "İkon seçin")}</strong>
          <small>{selected?.group ?? (value || "Hazır seçeneklerden belirleyin")}</small>
        </span>
        <ChevronDown size={17} aria-hidden="true" />
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => { setOpen(next); if (!next) setQuery(""); }}
        title="İkon seçin"
        description="Public sitede kullanılacak hazır ikonlardan birini seçin."
        size="lg"
      >
        <div className={styles.dialogBody}>
          <label className={styles.search} htmlFor={searchId}>
            <Search size={17} aria-hidden="true" />
            <input
              id={searchId}
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="İkon ara…"
            />
          </label>
          <div className={styles.grid} role="listbox" aria-label="Hazır ikonlar">
            <button
              type="button"
              className={`${styles.option} ${value === "" ? styles.active : ""}`}
              role="option"
              aria-selected={value === ""}
              onClick={() => select("")}
            >
              <span className={styles.emptyIcon}>—</span>
              <span><strong>İkon yok</strong><small>Boş bırak</small></span>
            </button>
            {filtered.map((option) => {
              const OptionIcon = option.icon;
              const active = option.value === value;
              return (
                <button
                  type="button"
                  className={`${styles.option} ${active ? styles.active : ""}`}
                  role="option"
                  aria-selected={active}
                  key={option.value}
                  onClick={() => select(option.value)}
                >
                  <OptionIcon size={21} aria-hidden="true" />
                  <span><strong>{option.label}</strong><small>{option.group}</small></span>
                  {active ? <Check className={styles.check} size={16} aria-hidden="true" /> : null}
                </button>
              );
            })}
          </div>
          {filtered.length === 0 ? <p className={styles.empty}>Eşleşen ikon bulunamadı.</p> : null}
        </div>
      </Dialog>
    </>
  );
}
